import React, { FC, useState, useEffect, useContext } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';

import BackflowDeviceForm from './BackflowDeviceForm';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';

import { backflowJobItemRequiredFields } from '../../backflowJobLists/constants/requiredFields';
import validateRequiredFields from '../../../utils/validateRequiredFields';

import { BackflowJobItem } from '../../model';

import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

import axios from '../../../sharedModules/utils/axios.utils';
import { persistDevice } from '../../utils/backflowCache';

interface BackflowDeviceEditProps {
  backflowJobItem: BackflowJobItem;
  onSave: () => void;
  onCancel: () => void;
}

const BackflowDeviceEdit: FC<BackflowDeviceEditProps> = ({ backflowJobItem, onSave, onCancel }) => {
  const { setSnackBarOpen, setSnackBarMessage, handleSnackbarAlertSeverityChange } = useContext(SnackBarContext);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [formValues, setFormValues] = useState<BackflowJobItem>(backflowJobItem);
  const [loading, setLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string>('');
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  useEffect(() => {
    if (backflowJobItem?.id) {
      setFormValues(backflowJobItem);
    }
  }, [backflowJobItem]);

  // Monitor online/offline status
  useEffect(() => {
    const updateOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  const handleUpdate = async () => {
    const validate = validateRequiredFields(formValues, backflowJobItemRequiredFields);
    setValidationErrors(validate.message);
    if (!validate.isValid) return;

    const assetNo = formValues?.asset_no || '';
    if (assetNo.length > 20 || /\s/.test(assetNo)) {
      setValidationErrors('Asset # must be a single word with no spaces and 20 characters or fewer');
      return;
    }

    setLoading(true);

    // If offline, update cache only
    if (!isOnline) {
      const success = persistDevice(backflowJobItem?.backflow_job_group_id, backflowJobItem?.id, formValues, true);

      if (success) {
        setSnackBarMessage('Updated device info on your device. Changes will sync when you have connectivity.');
        handleSnackbarAlertSeverityChange('info');
        setSnackBarOpen(true);
        setErrorMessage('');
        onSave();
      } else {
        setErrorMessage('Job group not cached. Please visit the job group page while online first.');
      }

      setLoading(false);
      return;
    }

    // If online, save to server
    try {
      await axios.put(`/backflows/backflow_job_items/${backflowJobItem.id}`, {
        backflow_job_item: formValues,
      });

      // Also update cache with fresh data (mark as synced)
      persistDevice(backflowJobItem?.backflow_job_group_id, backflowJobItem?.id, formValues, false);

      setSnackBarMessage('Updated Backflow Device Info');
      handleSnackbarAlertSeverityChange('success');
      setSnackBarOpen(true);
      setErrorMessage('');
      onSave();
    } catch (error) {
      setErrorMessage('Error Updating Backflow Device Info');
      setSnackBarMessage('Error Updating Backflow Device Info');
      handleSnackbarAlertSeverityChange('error');
      setSnackBarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoadingWrapper isLoading={loading}>
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <BackflowDeviceForm formValues={formValues} setFormValues={setFormValues} />
            </Grid>
            <Grid item xs={12}>
              <ErrorMessage message={errorMessage || validationErrors} />
            </Grid>
            {!isOnline && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  You are currently offline. Changes will be saved to your device and can be submitted when you are
                  connected.
                </Alert>
              </Grid>
            )}
            <Grid item>
              <Button variant="contained" onClick={handleUpdate}>
                {isOnline ? 'Update' : 'Save to Device'}
              </Button>
            </Grid>
            <Grid item>
              <Button variant="contained" color="secondary" onClick={onCancel}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </LoadingWrapper>
  );
};

export default BackflowDeviceEdit;
